import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { PageHeader } from "@/components/page-header";
import { ProfileInfoCard } from "@/components/profile-info-card";
import SignInActivityItem from "@/components/sign-in-activity-item";
import { Text } from "@/components/ui/typography";
import {
  groupSignInsByDate,
  type SignInGroup,
  type SignInRecord,
} from "@/lib/signins";
import { trpc } from "@/utils/trpc";

type WorkerSignIn = {
  id: string;
  siteName: string;
  signInTime: Date | string | number;
  signOutTime?: Date | string | number | null;
  method: string;
  flagged?: boolean;
  distanceFromSite?: number | null;
};

export default function WorkerSignIns() {
  const { id } = useParams();
  const workerId = id || "";
  const profile = useQuery(
    trpc.getWorkerProfile.queryOptions({ id: workerId })
  );
  const signins = useQuery(trpc.getWorkerSignIns.queryOptions({ workerId }));

  const records: SignInRecord[] =
    ((signins.data as WorkerSignIn[]) || []).map((r) => ({
      id: r.id,
      userId: workerId,
      workerName: r.siteName,
      jobTitle: null,
      signInTime: r.signInTime,
      signOutTime: r.signOutTime,
      method: r.method,
      flagged: r.flagged,
      distanceFromSite: r.distanceFromSite,
    })) || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={profile.data?.fullName ?? "Worker Sign-ins"}
        description="Sign-in history"
        backTo={`/workers/${workerId}`}
      />
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 lg:gap-8 items-start">
        <div className="lg:sticky lg:top-24">
          {profile.data && <ProfileInfoCard profile={profile.data} />}
        </div>
        <SignInContent
          isLoading={signins.isLoading}
          error={signins.error as Error | null}
          records={records}
        />
      </div>
    </div>
  );
}

function SignInContent({
  isLoading,
  error,
  records,
}: {
  isLoading: boolean;
  error: Error | null;
  records: SignInRecord[];
}) {
  if (isLoading)
    return <div className="text-muted-foreground">Loading sign-ins…</div>;
  if (error) return <div className="text-red-600">{error.message}</div>;
  if (!records || records.length === 0)
    return <div className="font-bold">No sign-ins yet.</div>;

  const groups = groupSignInsByDate(records);
  return <SignInList groups={groups} />;
}

function SignInList({ groups }: { groups: SignInGroup[] }) {
  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <SignInGroupView key={g.ts} group={g} />
      ))}
    </div>
  );
}

function SignInGroupView({ group }: { group: SignInGroup }) {
  return (
    <div className="space-y-2">
      <Text variant="label" tone="muted" as="p">
        {group.label}
      </Text>
      <ul className="divide-y rounded-md border bg-card">
        {group.items.map((r) => (
          <li key={r.id} className="p-4">
            <SignInActivityItem
              title={r.workerName}
              subtitle={r.jobTitle ?? undefined}
              signInTime={r.signInTime}
              signOutTime={r.signOutTime}
              method={r.method}
              flagged={r.flagged}
              distanceFromSite={r.distanceFromSite ?? undefined}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}
